/**
 * Daily Allowance model. Pure running balance:
 *   availableToday = rollover + incomeToDate − spendingToDate
 *
 * No PYF, no scheduled-bill reservation, no bucket allocation. The
 * Daily model trusts the user to pay bills via transactions; what
 * lands as a transaction is what counts.
 */

import type { AppData } from '../../data/types';
import type { PayPeriod } from '../payPeriod';
import {
  computeRolloverAmount,
  resolvePeriodIncome,
  roundMoney,
  sumExpensesInRange,
  sumIncomeInRange,
  sumSpendingWithScheduledInRange,
} from './shared';

export type DailyStatus =
  | 'on-track'
  | 'under-budget'
  | 'over-budget'
  | 'spent';

export interface DailyModelResult {
  period: PayPeriod;
  /** Running balance: rollover + incomeToDate − spendingToDate. */
  availableToday: number;
  /** Daily income rate for display: periodIncome / totalDays. */
  dailyIncomeRate: number;
  /** Cash position pool: periodIncome − spentThisPeriod. */
  remainingThisPeriod: number;
  dayInPeriod: number;
  totalDays: number;
  daysRemaining: number;
  spentToday: number;
  spentThisPeriod: number;
  status: DailyStatus;
  statusLabel: string;
  periodIncome: number;
  rolloverAmount: number;
}

function classify(
  available: number,
  rate: number,
): { status: DailyStatus; statusLabel: string } {
  if (Math.abs(available) <= 0.005) {
    return { status: 'spent', statusLabel: 'Budget spent for today' };
  }
  if (available < 0) {
    return { status: 'over-budget', statusLabel: 'Over budget today' };
  }
  if (rate > 0 && available > rate) {
    return { status: 'under-budget', statusLabel: 'Great day to save' };
  }
  return { status: 'on-track', statusLabel: "You're on track" };
}

export function calculateDaily(
  data: AppData,
  period: PayPeriod,
  today: Date,
): DailyModelResult {
  const periodIncome = resolvePeriodIncome(data, period);
  const rolloverAmount = computeRolloverAmount(data, period);

  const incomeToDate = sumIncomeInRange(data.transactions, period.start, today);
  // Past scheduled bills auto-deduct on their due date — included here
  // alongside transaction-based spending so a bill due today reduces
  // the running balance today without a manual log entry.
  const spendingToDate = sumSpendingWithScheduledInRange(
    data,
    period.start,
    today,
  );

  const availableToday = roundMoney(
    rolloverAmount + incomeToDate - spendingToDate,
  );
  const dailyIncomeRate =
    period.totalDays > 0 ? roundMoney(periodIncome / period.totalDays) : 0;
  const remainingThisPeriod = roundMoney(periodIncome - spendingToDate);

  const spentToday = sumExpensesInRange(data.transactions, today, today);
  const spentThisPeriod = spendingToDate;
  const { status, statusLabel } = classify(availableToday, dailyIncomeRate);

  return {
    period,
    availableToday,
    dailyIncomeRate,
    remainingThisPeriod,
    dayInPeriod: period.dayInPeriod,
    totalDays: period.totalDays,
    daysRemaining: period.daysRemaining,
    spentToday,
    spentThisPeriod,
    status,
    statusLabel,
    periodIncome,
    rolloverAmount,
  };
}

export { classify as _classifyDailyStatus };
